import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';

const demoJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Demo Talep Et | VergeIO',
  url: `${SITE_URL}/demo`,
  description: "VergeOS'u canlı gösterimde keşfedin. Mimari anlatım, canlı demo ve S&C oturumu ile altyapı ihtiyaçlarınıza özel öneriler alın.",
};

export default function DemoPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.company) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('email', formData.email);
      body.append('firstName', formData.firstName);
      body.append('lastName', formData.lastName);
      body.append('company', formData.company);
      await fetch('https://readdy.ai/api/form/d719443k1jkj467r5bc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Demo Talep Edin | VergeOS Canlı Gösterim"
        description="VergeOS'u canlı gösterimde keşfedin. Mimari anlatım, canlı demo ve S&C oturumu ile altyapı ihtiyaçlarınıza özel öneriler alın. Ücretsiz, satış araması gerektirmez."
        keywords="VergeOS demo, canlı gösterim, altyapı demo, sanallaştırma demo, VMware alternatifi demo"
        canonicalPath="/demo"
        jsonLd={demoJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{ backgroundImage: "url('https://www.verge.io/wp-content/uploads/bb-plugin/cache/vergeio-background-hero-landscape.jpeg')" }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">Demo Talep Et</h1>
          <h2 className="text-xl text-white/90 font-medium">VergeIO'yu kendiniz görün.</h2>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#f7f8fa]">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left */}
          <div>
            <h3 className="text-2xl font-bold text-[#1a1a2e] mb-4">
              VergeIO'nun neler yapabileceğini size göstermek istiyoruz.
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Mimari yapımızın beyaz tahta anlatımına ve Çözüm Mimarlarımızdan biriyle canlı bir gösterime katılmak için kaydolun.
            </p>
            <ul className="space-y-3">
              {[
                'Diğer alternatiflere kıyasla bizi benzersiz kılan özellikleri öğrenin',
                'VergeOS\'un çok çeşitli donanımlarda çalışmasını izleyerek esnekliğimizi ve verimliliğimizi deneyimleyin',
                'Arayüzü keşfedin, sanal makineler oluşturun ve operasyonları basitleştirin',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-[#e8600a] text-lg"></i>
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              {[
                { stat: '%85', label: 'Maliyet Azalması' },
                { stat: '7 dk', label: 'Kurulum Süresi' },
                { stat: '4x', label: 'Tekilleştirme' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-[#e8600a]">{s.stat}</div>
                  <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-xl border border-gray-100 p-8">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-green-50 rounded-full">
                  <i className="ri-checkbox-circle-line text-green-500 text-4xl"></i>
                </div>
                <h4 className="text-xl font-bold text-[#1a1a2e] mb-2">Talebiniz Alındı!</h4>
                <p className="text-gray-500 text-sm">En kısa sürede sizinle iletişime geçeceğiz.</p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-6 bg-[#e8600a] text-white px-6 py-2 rounded-md text-sm font-semibold hover:bg-[#cc5208] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ana Sayfaya Dön
                </button>
              </div>
            ) : (
              <form
                id="demo-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="İş E-posta Adresiniz"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Adınız"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Soyadınız"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Şirket Adı"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                    required
                  />
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#e8600a] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#cc5208] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? 'Gönderiliyor...' : 'Demo Talep Et'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <h2 className="text-2xl font-bold text-[#1a1a2e] text-center mb-10">Demo'da Sizi Neler Bekliyor?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-presentation-line',
                title: 'Mimari Anlatım',
                desc: 'VergeOS\'un diğer platformlardan farklı kılan birleşik mimarisini beyaz tahta üzerinde anlıyoruz.',
              },
              {
                icon: 'ri-play-circle-line',
                title: 'Canlı Gösterim',
                desc: 'VergeOS\'un gerçek bir ortamda çalıştığını izleyin; VM oluşturma, anlık görüntü alma ve daha fazlası.',
              },
              {
                icon: 'ri-question-answer-line',
                title: 'S&C Oturumu',
                desc: 'Çözüm Mimarlarımızla birebir soru-cevap yaparak altyapı ihtiyaçlarınıza özel öneriler alın.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-[#f7f8fa] rounded-xl p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-orange-50 rounded-full">
                  <i className={`${item.icon} text-[#e8600a] text-2xl`}></i>
                </div>
                <h4 className="font-bold text-[#1a1a2e] mb-2">{item.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-14 bg-[#1a1a2e]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <p className="text-white/90 text-lg italic leading-relaxed mb-6">
            "VergeIO'yu kurduğumuzdan bu yana donanım ve yazılım maliyetlerinde %50 azalma, 
            dönüştüren her müşteride aylık marjda iki kat artış ve müşterilerimiz için günlük 
            destek görevlerinde en az %30 azalma gördük."
          </p>
          <div className="text-[#e8600a] font-semibold">Nate Battles</div>
          <div className="text-white/60 text-sm">Yönetici Ortak, ZebraHost</div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
